import { BrowserRouter, Routes, Route, Navigate, useNavigate } from 'react-router-dom'
import { AuthProvider } from '@/shared/contexts/AuthContext'
import { ThemeProvider } from '@/shared/contexts/ThemeContext'
import { LoginForm } from '@/features/auth/components/LoginForm'
import { ResetPasswordPage } from '@/features/auth/components/ResetPasswordPage'
import { LandingPage } from '@/features/landing/components/LandingPage'
import { SignatureVerifier } from '@/features/signature-verification'
import VerificationLookup from '@/features/signature-verification/components/VerificationLookup'
import { BlogList } from '@/features/blog/components/BlogList'
import { BlogPost } from '@/features/blog/components/BlogPost'
import { TermsPage } from '@/features/legal/components/TermsPage'
import { PrivacyPage } from '@/features/legal/components/PrivacyPage'
import { BlockchainLookup } from '@/features/blockchain-lookup/components/BlockchainLookup'
import { DocumentCustody, CustodyLookup } from '@/features/document-custody'
import { QRGenerator } from '@/features/qr-generator'
import { ElectronicSigningPage, ElectronicSignatureLookup, DigitalSignatureLookup, UnifiedSignatureLookup } from '@/features/electronic-signing'
import { Dashboard } from '@/features/dashboard'
import { MySignatures } from '@/features/my-signatures'
import { DocumentsPage, CreateEnvelope, EnvelopeDetail } from '@/features/documents'
import { SignInvitation } from '@/features/sign-invitation'
import { InvitePage } from '@/features/invite'
import { DashboardLayout } from '@/shared/components/layout/DashboardLayout'
import ProtectedRoute from '@/shared/components/ProtectedRoute'

function LandingPageWrapper() {
  const navigate = useNavigate()
  // La landing publica siempre se muestra, incluso con sesion activa, para
  // que click en el logo del header desde dashboard vuelva aca. Si vienen
  // sin sesion el CTA del Header dira 'Ingresar'; con sesion mostrara el
  // user menu y un link a /dashboard.
  return <LandingPage onStartVerification={() => navigate('/verifier')} />
}

function DashboardPageWrapper() {
  const navigate = useNavigate()
  return (
    <DashboardLayout onNavigateHome={() => navigate('/dashboard')}>
      <Dashboard />
    </DashboardLayout>
  )
}

function VerifierPage() {
  const navigate = useNavigate()
  return (
    <DashboardLayout onNavigateHome={() => navigate('/')}>
      <div className="max-w-5xl mx-auto">
        <SignatureVerifier />
      </div>
    </DashboardLayout>
  )
}

function BlogPageWrapper() {
  const navigate = useNavigate()
  return (
    <DashboardLayout onNavigateHome={() => navigate('/')}>
      <BlogList />
    </DashboardLayout>
  )
}

function BlogPostPageWrapper() {
  const navigate = useNavigate()
  return (
    <DashboardLayout onNavigateHome={() => navigate('/')}>
      <BlogPost />
    </DashboardLayout>
  )
}

function BlockchainLookupPageWrapper() {
  const navigate = useNavigate()
  return (
    <DashboardLayout onNavigateHome={() => navigate('/')}>
      <BlockchainLookup />
    </DashboardLayout>
  )
}

function CustodyPageWrapper() {
  const navigate = useNavigate()
  return (
    <DashboardLayout onNavigateHome={() => navigate('/')}>
      <div className="max-w-5xl mx-auto">
        <DocumentCustody />
      </div>
    </DashboardLayout>
  )
}

function QRGeneratorPageWrapper() {
  const navigate = useNavigate()
  return (
    <DashboardLayout onNavigateHome={() => navigate('/')}>
      <div className="max-w-5xl mx-auto">
        <QRGenerator />
      </div>
    </DashboardLayout>
  )
}

function MySignaturesPageWrapper() {
  const navigate = useNavigate()
  return (
    <DashboardLayout onNavigateHome={() => navigate('/')}>
      <MySignatures />
    </DashboardLayout>
  )
}

function DocumentsPageWrapper() {
  const navigate = useNavigate()
  return (
    <DashboardLayout onNavigateHome={() => navigate('/')}>
      <DocumentsPage />
    </DashboardLayout>
  )
}

function CreateEnvelopePageWrapper() {
  const navigate = useNavigate()
  return (
    <DashboardLayout onNavigateHome={() => navigate('/')}>
      <CreateEnvelope />
    </DashboardLayout>
  )
}

function EnvelopeDetailPageWrapper() {
  const navigate = useNavigate()
  return (
    <DashboardLayout onNavigateHome={() => navigate('/')}>
      <EnvelopeDetail />
    </DashboardLayout>
  )
}

function ElectronicSigningPageWrapper() {
  const navigate = useNavigate()
  return (
    <DashboardLayout onNavigateHome={() => navigate('/')}>
      <ElectronicSigningPage />
    </DashboardLayout>
  )
}

function InvitePageWrapper() {
  const navigate = useNavigate()
  return (
    <DashboardLayout onNavigateHome={() => navigate('/')}>
      <InvitePage />
    </DashboardLayout>
  )
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            {/* Public routes */}
            <Route path="/" element={<LandingPageWrapper />} />
            <Route path="/login" element={<LoginForm />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/verify/:id" element={<VerificationLookup />} />
            <Route path="/se/:id" element={<ElectronicSignatureLookup />} />
            <Route path="/s/:id" element={<DigitalSignatureLookup />} />
            <Route path="/v/:shortId" element={<UnifiedSignatureLookup />} />
            <Route path="/d/:id" element={<CustodyLookup />} />
            <Route path="/blog" element={<BlogPageWrapper />} />
            <Route path="/blog/:slug" element={<BlogPostPageWrapper />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/firmar-invitacion/:token" element={<SignInvitation />} />

            {/* Protected routes - require authentication */}
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <DashboardPageWrapper />
                </ProtectedRoute>
              }
            />
            <Route
              path="/verifier"
              element={
                <ProtectedRoute>
                  <VerifierPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/custody"
              element={
                <ProtectedRoute>
                  <CustodyPageWrapper />
                </ProtectedRoute>
              }
            />
            <Route
              path="/qr-generator"
              element={
                <ProtectedRoute>
                  <QRGeneratorPageWrapper />
                </ProtectedRoute>
              }
            />
            <Route
              path="/blockchain-lookup"
              element={
                <ProtectedRoute>
                  <BlockchainLookupPageWrapper />
                </ProtectedRoute>
              }
            />
            <Route
              path="/firmar"
              element={
                <ProtectedRoute>
                  <ElectronicSigningPageWrapper />
                </ProtectedRoute>
              }
            />
            <Route
              path="/firmar-electronica"
              element={<Navigate to="/firmar" replace />}
            />
            <Route
              path="/mis-firmas"
              element={
                <ProtectedRoute>
                  <MySignaturesPageWrapper />
                </ProtectedRoute>
              }
            />
            <Route
              path="/documentos"
              element={
                <ProtectedRoute>
                  <DocumentsPageWrapper />
                </ProtectedRoute>
              }
            />
            <Route
              path="/documentos/nuevo"
              element={
                <ProtectedRoute>
                  <CreateEnvelopePageWrapper />
                </ProtectedRoute>
              }
            />
            <Route
              path="/documentos/:id"
              element={
                <ProtectedRoute>
                  <EnvelopeDetailPageWrapper />
                </ProtectedRoute>
              }
            />
            <Route
              path="/invitar"
              element={
                <ProtectedRoute>
                  <InvitePageWrapper />
                </ProtectedRoute>
              }
            />

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  )
}

export default App
